import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { LayoutDashboard, Type, Check } from 'lucide-react';

import BaseModal from '@/shared/components/modals/core/BaseModal.jsx';
import Input from '@/shared/components/elements/Input';
import Button from '@/shared/components/elements/Button';
import { dashboardNameSchema } from '@/feature/Dashboard/schemas/dashboardSchemas';
import { extractFieldErrors } from '@/shared/utils/errorUtils';

function RenameDashboardModal({
  isOpen,
  onClose,
  onSubmit,
  dashboard = null,
  isLoading = false,
}) {
  const [serverErrors, setServerErrors] = useState({});

  const form = useForm({
    defaultValues: {
      name: dashboard?.name || '',
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        await onSubmit({
          name: value.name,
          dashboard,
        });
        form.reset();
        onClose();
      } catch (error) {
        console.error('Rename dashboard error:', error);
        const fieldErrors = extractFieldErrors(error);
        if (fieldErrors && Object.keys(fieldErrors).length > 0) {
          setServerErrors(fieldErrors);
        }
      }
    },
  });

  useEffect(() => {
    if (isOpen && dashboard) {
      form.setFieldValue('name', dashboard.name);
      setServerErrors({});
    }
  }, [isOpen, dashboard, form]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="تغییر نام داشبورد"
      icon={<LayoutDashboard size={20} />}
      iconVariant="primary"
      size="md"
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            icon={<Check size={18} />}
            iconPosition="left"
            onClick={handleFormSubmit}
          >
            ذخیره تغییرات
          </Button>
        </>
      }
    >
      <form onSubmit={handleFormSubmit} className="space-y-5 p-6">
        <form.Field
          name="name"
          validators={{
            onChange: dashboardNameSchema.shape.name,
          }}
        >
          {(field) => {
            const rawError = field.state.meta.errors?.[0];
            const errorMessage = rawError
              ? typeof rawError === 'string'
                ? rawError
                : rawError.message || 'خطای اعتبارسنجی'
              : serverErrors.name;

            return (
              <Input
                label="نام داشبورد"
                type="text"
                placeholder="نام داشبورد را وارد کنید..."
                value={field.state.value}
                onChange={(e) => {
                  field.handleChange(e.target.value);

                  if (serverErrors.name) {
                    setServerErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }
                }}
                error={errorMessage}
                icon={<Type size={18} />}
                disabled={isLoading}
                maxLength={100}
              />
            );
          }}
        </form.Field>
      </form>
    </BaseModal>
  );
}

RenameDashboardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dashboard: PropTypes.shape({
    name: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
};

export default RenameDashboardModal;
